import { useId, useState } from "react";
import { BadgeCheck } from "lucide-react";
import { VERIFIED_BADGES, type VerifiedBadgeId } from "@shared/constants/community";
import type { BadgeGrant } from "@shared/types/communityAdmin";
import { Button } from "../../../components/ui/Button";
import { Card } from "../../../components/ui/Card";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { EmptyState } from "../../../components/ui/EmptyState";
import { PageLoader } from "../../../components/ui/Spinner";
import { cn } from "../../../lib/cn";
import { INPUT_CLASS } from "../../../lib/forms/inputClass";
import { useToast } from "../../../lib/toast/ToastProvider";
import { BadgeChip } from "../../_sim/ui/BadgeChip";
import { BADGES } from "../../_sim/people";
import { useBadgeGrants, useGrantBadge, useRevokeBadge } from "./api";

/**
 * Platform console → Badges. Grant or revoke Verified Nurse / Doctor / Teacher
 * by account email, after the team has checked the credential (never store the
 * license number — the note says how it was checked). Founding Member is
 * automatic and isn't granted here.
 */
export function BadgesPanel() {
  const { data, isLoading, error } = useBadgeGrants();
  const grant = useGrantBadge();
  const revoke = useRevokeBadge();
  const { publish } = useToast();
  const [email, setEmail] = useState("");
  const [badge, setBadge] = useState<VerifiedBadgeId>("verifiedNurse");
  const [note, setNote] = useState("");
  const [revoking, setRevoking] = useState<BadgeGrant | null>(null);
  const emailId = useId();
  const badgeId = useId();
  const noteId = useId();

  return (
    <div className="space-y-6">
      <Card className="p-4 sm:p-5">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-neutral-900">
          <BadgeCheck className="h-5 w-5 text-emerald-600" aria-hidden />
          Grant a verified badge
        </h2>
        <p className="mt-1 text-sm text-neutral-600">
          Only after the credential is checked: license against official records, plus workplace ID or a call.
          Write how it was checked — never the license number.
        </p>
        <form
          className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]"
          onSubmit={(e) => {
            e.preventDefault();
            if (!email.trim()) return;
            grant.mutate(
              { email: email.trim(), badge, note: note.trim() || undefined },
              {
                onSuccess: (g) => {
                  setEmail("");
                  setNote("");
                  publish({ variant: "success", title: `${BADGES[g.badge].label} granted to ${g.user.displayName}` });
                },
                onError: (err) => publish({ variant: "error", title: "Couldn't grant that badge", description: err.message }),
              },
            );
          }}
        >
          <div>
            <label htmlFor={emailId} className="block text-sm font-medium text-neutral-800">
              Account email
            </label>
            <input
              id={emailId}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={INPUT_CLASS}
              placeholder="name@example.com"
            />
          </div>
          <div>
            <label htmlFor={badgeId} className="block text-sm font-medium text-neutral-800">
              Badge
            </label>
            <select
              id={badgeId}
              value={badge}
              onChange={(e) => setBadge(e.target.value as VerifiedBadgeId)}
              className={INPUT_CLASS}
            >
              {VERIFIED_BADGES.map((b) => (
                <option key={b} value={b}>
                  {BADGES[b].label}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor={noteId} className="block text-sm font-medium text-neutral-800">
              How it was checked <span className="font-normal text-neutral-600">(optional)</span>
            </label>
            <input
              id={noteId}
              value={note}
              maxLength={300}
              onChange={(e) => setNote(e.target.value)}
              className={INPUT_CLASS}
              placeholder="e.g. License verified online; workplace confirmed by phone"
            />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" isLoading={grant.isPending} disabled={!email.trim()}>
              Grant badge
            </Button>
          </div>
        </form>
      </Card>

      <section aria-label="Verified members">
        {isLoading ? (
          <PageLoader label="Loading badges…" />
        ) : error ? (
          <Card className="p-6 text-sm text-red-700">Couldn&apos;t load badges.</Card>
        ) : !data || data.items.length === 0 ? (
          <EmptyState title="No verified badges yet" description="Badges you grant appear here." />
        ) : (
          <ul className="space-y-2">
            {data.items.map((g) => (
              <li key={g.id}>
                <Card className={cn("flex flex-wrap items-center gap-3 p-4")}>
                  <BadgeChip badge={g.badge} />
                  {/* The details ask for 12rem before shrinking, so on a phone they take their own line. */}
                  <div className="min-w-0 flex-[1_1_12rem]">
                    <p className="text-sm font-semibold text-neutral-900">{g.user.displayName}</p>
                    <p className="truncate text-xs text-neutral-600">
                      {g.user.email} · since {new Date(g.grantedAt).toLocaleDateString()}
                      {g.grantedByEmail ? ` · by ${g.grantedByEmail}` : ""}
                    </p>
                    {g.note && <p className="mt-0.5 text-xs text-neutral-600">{g.note}</p>}
                  </div>
                  <Button size="sm" variant="destructiveOutline" onClick={() => setRevoking(g)}>
                    Revoke
                  </Button>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (!open) setRevoking(null);
        }}
        title="Revoke this badge?"
        description={
          revoking
            ? `${revoking.user.displayName} will no longer show ${BADGES[revoking.badge].label}. The grant stays in the record.`
            : ""
        }
        confirmLabel="Revoke badge"
        destructive
        isPending={revoke.isPending}
        onConfirm={() => {
          if (!revoking) return;
          revoke.mutate(
            { grantId: revoking.id },
            {
              onSuccess: () => {
                setRevoking(null);
                publish({ variant: "success", title: "Badge revoked", duration: 2500 });
              },
              onError: (err) => publish({ variant: "error", title: "Couldn't revoke", description: err.message }),
            },
          );
        }}
      />
    </div>
  );
}
